import { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { Button } from "./atoms";
import { ConfirmDeleteModal } from "./molecules/ConfirmDeleteModal";
import { deleteEmployee } from "../api/employees";
import type { EmployeeFormData } from "./EmployeeForm";

/**
 * 子ページ（EmployeeFormPage / TagFormPage など）が
 * 共通ヘッダーへエラー表示するための Outlet context 型。
 *
 * invalidFields には API 側のフィールドキー（`code`, `name`, `store_id` 等）を入れる。
 * pendingFormState は Step 間で引き回すフォームデータ（サイドバー遷移でも維持）。
 */
export interface EmployeeFormOutletContext {
    setPageError: (message: string | null) => void;
    invalidFields: string[];
    setInvalidFields: (fields: string[]) => void;
    pendingFormState: EmployeeFormData | null;
    setPendingFormState: (state: EmployeeFormData | null) => void;
}

/** 「基本情報」タブに含まれる必須フィールドのキー一覧 */
const BASIC_INFO_REQUIRED_FIELDS = [
    "code",
    "name",
    "store_id",
    "department_id",
    "employment_type_id",
] as const;

interface MenuItem {
    id: string;
    label: string;
    path: string;
    displayLabel: string;
}

const EmployeeEditLayout = () => {
    const { t } = useTranslation("common");
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams<{ id?: string }>();
    const isEdit = !!id;

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    // 子ページから渡されるエラー（タイトル直下に表示）
    const [pageError, setPageError] = useState<string | null>(null);
    // 必須チェックで未入力だったフィールドキーの一覧（タブ文字色・フィールド赤枠表示用）
    const [invalidFields, setInvalidFields] = useState<string[]>([]);
    // Step 間で引き回すフォーム state（サイドバー遷移でも維持。対象従業員が変わったらリセット）
    const [pendingFormState, setPendingFormState] = useState<EmployeeFormData | null>(null);

    // 画面遷移時は子ページのエラーを自動リセット（invalidFields は残す: ユーザーが修正しに step 1 へ戻るため）
    useEffect(() => {
        setPageError(null);
    }, [location.pathname]);

    // 対象従業員が変わったら pendingFormState もリセット
    useEffect(() => {
        setPendingFormState(null);
        setInvalidFields([]);
    }, [id, isEdit]);

    // タブの赤表示: invalidFields に含まれ、かつ pendingFormState でも未入力の場合に true
    // （ユーザーが修正した瞬間に自動で解除される）
    const hasBasicInfoError = useMemo(() => {
        const basicKeys = BASIC_INFO_REQUIRED_FIELDS as readonly string[];
        return invalidFields.some((f) => {
            if (!basicKeys.includes(f)) return false;
            if (!pendingFormState) return true;
            const value = pendingFormState[f as keyof EmployeeFormData];
            if (typeof value === "boolean") return false;
            return !value || (typeof value === "string" && !value.trim());
        });
    }, [invalidFields, pendingFormState]);

    const menuItems = useMemo<MenuItem[]>(() => {
        const base = isEdit ? `/employee/info/${id}` : "/employee/info/new";
        const basicPath = isEdit ? `${base}/edit` : base;
        return [
            { id: "1", label: "基本情報", path: basicPath, displayLabel: "1. 基本情報" },
            { id: "2", label: "契約情報（個人）", path: `${base}/work-rules`, displayLabel: "2. 契約情報（個人）" },
            { id: "3", label: "応援", path: `${base}/support`, displayLabel: "3. 応援" },
            { id: "4", label: "タグ", path: `${base}/tags`, displayLabel: "4. タグ" },
        ];
    }, [id, isEdit]);

    const activeMenuId = useMemo(() => {
        const { pathname } = location;
        if (pathname.endsWith("/work-rules")) return "2";
        if (pathname.endsWith("/support")) return "3";
        if (pathname.endsWith("/tags")) return "4";
        return "1";
    }, [location]);

    const handleDelete = async () => {
        if (!id) return;
        const employeeId = parseInt(id, 10);
        if (Number.isNaN(employeeId)) return;
        setDeleting(true);
        setDeleteError(null);
        try {
            await deleteEmployee(employeeId);
            navigate("/employee/info");
        } catch (err) {
            console.error("Failed to delete employee:", err);
            setDeleteError(err instanceof Error ? err.message : "削除に失敗しました");
            setDeleting(false);
        }
    };

    return (
        <div className="flex-1 flex flex-col overflow-hidden">
            {/* ヘッダー */}
            <div className="px-6 py-4 flex-shrink-0">
                <div className="flex items-center gap-3">
                    <div className="w-1 h-6 bg-brand-500 rounded-full" />
                    <h1 className="text-xl font-bold text-gray-800">
                        {t("EMPLOYEE")}の登録・編集
                    </h1>
                </div>
                {(deleteError || pageError) && (
                    <div className="mt-2 text-sm text-red-600">{deleteError ?? pageError}</div>
                )}
            </div>

            {/* フォームエリア */}
            <div className="flex-1 overflow-hidden px-6 pb-6">
                <div className="flex h-full gap-4">
                    {/* サイドメニュー */}
                    <div className="w-44 bg-white rounded-lg py-4 flex-shrink-0 flex flex-col">
                        <nav className="space-y-1 flex-1">
                            {menuItems.map((item) => {
                                const hasError = item.id === "1" && hasBasicInfoError;
                                return (
                                <button
                                    key={item.id}
                                    type="button"
                                    onClick={() => navigate(item.path)}
                                    className={clsx(
                                        "w-full text-left px-4 py-2 text-sm",
                                        hasError
                                            ? "text-red-600 font-medium"
                                            : activeMenuId === item.id
                                                ? "text-brand-500 font-medium"
                                                : "text-gray-600 hover:text-gray-900"
                                    )}
                                >
                                    {item.displayLabel}
                                </button>
                                );
                            })}
                        </nav>
                        {isEdit && (
                            <div className="px-4 pt-4 mt-auto">
                                <Button
                                    variant="danger"
                                    size="md"
                                    onClick={() => setShowDeleteModal(true)}
                                    disabled={deleting}
                                    className="w-full"
                                >
                                    削除
                                </Button>
                            </div>
                        )}
                    </div>

                    <ConfirmDeleteModal
                        isOpen={showDeleteModal}
                        onCancel={() => setShowDeleteModal(false)}
                        onConfirm={() => {
                            setShowDeleteModal(false);
                            handleDelete();
                        }}
                        isDeleting={deleting}
                    />

                    {/* タブごとの内容 */}
                    <Outlet context={{
                        setPageError,
                        invalidFields,
                        setInvalidFields,
                        pendingFormState,
                        setPendingFormState,
                    } satisfies EmployeeFormOutletContext} />
                </div>
            </div>
        </div>
    );
};

export default EmployeeEditLayout;
